import { useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Activity,
  Coins,
  DollarSign,
  TrendingUp,
  MessageCircle,
  Share2,
  Music2,
  Globe,
  AlertTriangle,
  AtSign,
} from "lucide-react";
import { controlCenterService } from "@/services/control-center.service";
import { useAdminResource } from "@/hooks/useAdminResource";
import { count, usd, LoadState, Metric } from "./ControlCenter";

const icons = {
  whatsapp: MessageCircle,
  meta: Share2,
  tiktok: Music2,
  x: AtSign,
  google: Globe,
};
export function PlatformOverview({ refreshKey }: { refreshKey: number }) {
  const { data, loading, error, refresh } = useAdminResource(
    useCallback(() => {
      void refreshKey;
      return controlCenterService.overview();
    }, [refreshKey]),
  );
  return (
    <div className="space-y-4">
      <LoadState
        loading={loading}
        error={error}
        hasData={!!data}
        retry={refresh}
      />
      {data && (
        <>
          <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
            <Metric
              label="AI requests"
              value={count(data.ai.requests)}
              detail="This calendar month · UTC"
              icon={Activity}
              tone="info"
            />
            <Metric
              label="Platform AI cost"
              value={usd(data.ai.platformCostUsd)}
              detail="Recorded provider cost estimate"
              icon={DollarSign}
              tone="warning"
            />
            <Metric
              label="Credits charged"
              value={count(data.ai.credits)}
              detail={`${count(data.ai.reservedCredits)} reserved · this month`}
              icon={Coins}
              tone="success"
            />
            <Metric
              label="Gross margin"
              value={
                data.ai.grossMarginUsd == null
                  ? "Not recorded"
                  : usd(data.ai.grossMarginUsd)
              }
              detail={`${count(data.ai.financialRequests)} requests with charge records`}
              icon={TrendingUp}
            />
          </div>
          <section className="admin-panel overflow-hidden">
            <div className="flex items-center justify-between border-b border-border px-4 py-3">
              <h3 className="text-sm font-semibold">Connected accounts</h3>
              <Link to="/system-health" className="text-xs text-primary">
                System health →
              </Link>
            </div>
            <div className="grid grid-cols-2 divide-border xl:grid-cols-4">
              {data.connections.map((item) => {
                const Icon =
                  icons[item.provider as keyof typeof icons] ?? Globe;
                return (
                  <Link
                    key={item.provider}
                    to={
                      item.provider === "whatsapp"
                        ? "/whatsapp-health"
                        : `/integrations?group=${item.provider}`
                    }
                    className="flex items-center gap-3 border-b p-4 hover:bg-surface-alt xl:border-b-0"
                  >
                    <span
                      className={`rounded-lg p-2.5 ${item.alerts ? "bg-warning-soft text-warning" : item.provider === "whatsapp" ? "bg-primary-soft text-primary" : "bg-info-soft text-info"}`}
                    >
                      <Icon size={18} />
                    </span>
                    <div>
                      <p className="text-xs text-text-secondary">
                        {item.label}
                      </p>
                      <p className="mt-1 text-xl font-semibold">
                        {count(item.active)}{" "}
                        <span className="text-[11px] font-normal text-text-muted">
                          active
                        </span>
                      </p>
                    </div>
                    {item.alerts > 0 && (
                      <span className="ml-auto text-xs text-warning">
                        {item.alerts} alerts
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
            {data.webhookFailures.failed > 0 && (
              <Link
                to="/system-health"
                className="flex items-center gap-2 border-t border-border bg-warning-soft px-4 py-3 text-xs text-warning"
              >
                <AlertTriangle size={14} />
                {count(data.webhookFailures.failed)} webhook deliveries need
                attention · last 24 hours
                <span className="ml-auto">Review →</span>
              </Link>
            )}
          </section>
        </>
      )}
    </div>
  );
}
